/**
 * Dense table view — all 17 metrics in a single scrollable table.
 * Group rows (Delivery / Performance) are rendered as header rows.
 * Each data row is clickable to open the MetricDrawer.
 *
 * Reliability extension: the three gated-out statuses render with a neutral
 * grey status chip + short label (never red/green), gated-in metrics get a
 * small "measured/population" caption under the metric name, and a
 * "ramping" badge appears next to metrics still building history.
 */
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { Fragment, type ReactElement } from 'react';

import type { KpiStatus, Metric, ScorecardGroup } from '@/types/scorecard';
import { isNeutralStatus } from '@/types/scorecard';
import { Sparkline } from './Sparkline';

interface KpiTableProps {
  groups: ScorecardGroup[];
  onMetricClick: (metric: Metric) => void;
}

const STATUS_COLOR: Record<string, string> = {
  green: '#6BFFAB',
  amber: '#FFB000',
  red: '#ef4444',
  insufficient_data: '#8a8a94',
  not_available: '#8a8a94',
  not_tracked: '#8a8a94',
  na: '#6c6c78',
};

const CHIP_BG: Record<string, string> = {
  green: 'rgba(107,255,171,.10)',
  amber: 'rgba(255,176,0,.10)',
  red: 'rgba(239,68,68,.12)',
  insufficient_data: 'rgba(138,138,148,.10)',
  not_available: 'rgba(138,138,148,.10)',
  not_tracked: 'rgba(138,138,148,.10)',
  na: 'rgba(255,255,255,.04)',
};

const NEUTRAL_CHIP_LABEL: Record<string, string> = {
  insufficient_data: 'Insufficient data',
  not_available: 'Missing in Notion',
  not_tracked: 'Not tracked',
};

function chipLabel(status: KpiStatus): string {
  return NEUTRAL_CHIP_LABEL[status] ?? status;
}

const DELTA_ARROW: Record<string, string> = {
  good: '▲',
  bad: '▼',
  flat: '',
};

export function KpiTable({ groups, onMetricClick }: KpiTableProps): ReactElement {
  return (
    <Box
      sx={{
        overflowX: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
      }}
    >
      <Box
        component="table"
        sx={{
          borderCollapse: 'collapse',
          width: '100%',
          minWidth: 740,
        }}
        aria-label="KPI metrics table"
      >
        <Box component="thead">
          <Box component="tr">
            {['Metric', 'Value', 'Target', 'Status', 'vs prior', 'Trend'].map((h) => (
              <Box
                key={h}
                component="th"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.5625rem',
                  fontWeight: 700,
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                  textAlign: h === 'Value' || h === 'Target' || h === 'vs prior' ? 'right' : 'left',
                  p: '11px 16px',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  background: 'background.deeper',
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {groups.map((group) => (
            <Fragment key={group.key}>
              {/* Group header row */}
              <Box
                key={`grp-${group.key}`}
                component="tr"
                aria-label={`Group: ${group.label}`}
              >
                <Box
                  component="td"
                  colSpan={6}
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    color: 'primary.main',
                    background: 'background.deeper',
                    p: '8px 16px',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {group.label}
                </Box>
              </Box>

              {/* Metric rows */}
              {group.metrics.map((metric) => (
                <Box
                  key={metric.key}
                  component="tr"
                  onClick={() => onMetricClick(metric)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover td': { background: 'background.elev2' },
                    '&:focus-within': { outline: '2px solid', outlineColor: 'primary.main' },
                  }}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onMetricClick(metric); }}
                  aria-label={`${metric.label}: ${metric.display}. Click for detail.`}
                >
                  {/* Metric name + dot */}
                  <Box
                    component="td"
                    sx={{
                      p: '10px 16px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      fontSize: '0.8125rem',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: STATUS_COLOR[metric.status] ?? '#6c6c78',
                          boxShadow: metric.status !== 'na' && !isNeutralStatus(metric.status)
                            ? `0 0 6px ${STATUS_COLOR[metric.status] ?? '#6c6c78'}70`
                            : 'none',
                          flexShrink: 0,
                        }}
                        aria-hidden="true"
                      />
                      <Stack spacing={0.125}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Typography sx={{ fontWeight: 500, fontSize: '0.8125rem', color: 'text.primary' }}>
                            {metric.label}
                          </Typography>
                          {metric.ramping && (
                            <Tooltip title="Window still building history since the 14-Jun cutoff — RAG shown, but treat as provisional." placement="top">
                              <Box
                                component="span"
                                sx={{
                                  fontFamily: '"JetBrains Mono", monospace',
                                  fontSize: '0.5rem',
                                  fontWeight: 700,
                                  letterSpacing: '0.4px',
                                  textTransform: 'uppercase',
                                  color: 'primary.main',
                                  border: '1px solid',
                                  borderColor: 'primary.main',
                                  borderRadius: '3px',
                                  px: 0.5,
                                  py: '1px',
                                }}
                              >
                                Ramping
                              </Box>
                            </Tooltip>
                          )}
                        </Stack>
                        {metric.status !== 'na' && !isNeutralStatus(metric.status) && !metric.reliability.gated && (
                          <Typography
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '0.5625rem',
                              color: 'text.faintest',
                            }}
                          >
                            {metric.reliability.measured}/{metric.reliability.population} measured
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Value */}
                  <Box
                    component="td"
                    sx={{
                      p: '10px 16px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontWeight: 700,
                        fontSize: '0.9375rem',
                        color: metric.status === 'na' || isNeutralStatus(metric.status) ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {metric.display}
                    </Typography>
                  </Box>

                  {/* Target */}
                  <Box
                    component="td"
                    sx={{
                      p: '10px 16px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.75rem',
                        color: 'text.disabled',
                      }}
                    >
                      {metric.target.label}
                    </Typography>
                  </Box>

                  {/* Status chip */}
                  <Box
                    component="td"
                    sx={{
                      p: '10px 16px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Tooltip
                      title={metric.reliability.gate_reason ?? ''}
                      placement="top"
                      disableHoverListener={!metric.reliability.gate_reason}
                    >
                      <Box
                        sx={{
                          display: 'inline-block',
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.5625rem',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          px: 1,
                          py: 0.25,
                          borderRadius: '3px',
                          color: STATUS_COLOR[metric.status] ?? '#6c6c78',
                          background: CHIP_BG[metric.status] ?? 'rgba(255,255,255,.04)',
                        }}
                      >
                        {chipLabel(metric.status)}
                      </Box>
                    </Tooltip>
                  </Box>

                  {/* Delta */}
                  <Box
                    component="td"
                    sx={{
                      p: '10px 16px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        color:
                          metric.delta.kind === 'good'
                            ? '#6BFFAB'
                            : metric.delta.kind === 'bad'
                            ? '#ef4444'
                            : 'text.disabled',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                      }}
                    >
                      {DELTA_ARROW[metric.delta.kind] && (
                        <Box component="span" aria-hidden="true">
                          {DELTA_ARROW[metric.delta.kind]}
                        </Box>
                      )}
                      {metric.delta.display}
                    </Typography>
                  </Box>

                  {/* Sparkline */}
                  <Box
                    component="td"
                    sx={{
                      p: '10px 16px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ width: 66, height: 20, ml: 'auto' }}>
                      <Sparkline
                        data={metric.spark}
                        status={metric.status}
                        width={66}
                        height={20}
                        ariaLabel={`${metric.label} trend`}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/** Loading skeleton for the table view. */
export function KpiTableSkeleton(): ReactElement {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            gap: 2,
            p: '10px 16px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Skeleton variant="text" width="30%" height={14} />
          <Skeleton variant="text" width="15%" height={14} sx={{ ml: 'auto' }} />
          <Skeleton variant="text" width="10%" height={14} />
          <Skeleton variant="rectangular" width={50} height={14} sx={{ borderRadius: '3px' }} />
          <Skeleton variant="text" width="8%" height={14} />
          <Skeleton variant="rectangular" width={66} height={20} sx={{ borderRadius: '3px' }} />
        </Box>
      ))}
    </Box>
  );
}
